import { createContext, ReactElement, useReducer } from "react"
import axios from "utils/axios"
import { FETCH_COMPANIES, FETCH_COMPANY_BREAKDOWNS } from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import { CompaniesContextType, Company, CompanyBreakDown } from "types/companies"
import companiesReducer, { initialCompanyState } from "store/reducers/companies"

const CompaniesContext = createContext<CompaniesContextType | null>(null)

export const CompaniesProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(companiesReducer, initialCompanyState)

  const fetchCompanyList = async (
    limit: number,
    offset: number,
    search?: string,
    country?: string,
    idList?: number[],
    orderBy?: string
  ) => {
    const idListFilter = idList || []
    const searchParam = `${search ? `&search=${search}` : ""}`
    const countryParam = `${country ? `&country=${country}` : ""}`
    const orderByParam = `${orderBy ? `&order_by=${orderBy}` : ""}`
    const idListParam = `${idListFilter.length > 0 ? `&id_list=${idListFilter.join(",")}` : ""}`
    const response = await axios(
      `/companies/?limit=${limit}&offset=${offset}${searchParam}${countryParam}${idListParam}${orderByParam}`,
      {
        method: "get"
      }
    )

    dispatch({
      type: FETCH_COMPANIES,
      payload: {
        ...state,
        paginatedCompanyList: mapPaginatedDataToCamelCase<PaginatedData<Company>>(response.data)
      }
    })
  }

  const updateCompanyBlackListStatus = async (id: number, value: boolean) => {
    const response = await axios(`/companies/update_black_list/`, {
      method: "put",
      data: { id, status: value }
    })
    if (!response.data.success) {
      return
    }
    const updatedList = state.paginatedCompanyList.results.map((element) => {
      if (element.id === id) {
        element.blackList = value
      }
      return element
    })

    dispatch({
      type: FETCH_COMPANIES,
      payload: {
        ...state,
        paginatedCompanyList: { ...state.paginatedCompanyList, results: updatedList }
      }
    })
  }

  const fetchCompanyBreakdownList = async (
    limit: number,
    offset: number,
    blackListPage: boolean,
    search?: string,
    companyName?: string,
    companyId?: number,
    orderBy?: string
  ) => {
    const companyNameFilter = companyName ? `&company_name=${companyName}` : ""
    const searchFilter = search ? `&search=${search}` : ""
    const companyIdFilter = companyId ? `&company_id=${companyId}` : ""
    const blackListFilter = blackListPage ? `&black_list=1` : ""
    const orderByParam = `${orderBy ? `&order_by=${orderBy}` : ""}`
    const response = await axios(
      `/company/breakdown/?limit=${limit}&offset=${offset}${searchFilter}${companyNameFilter}${companyIdFilter}${blackListFilter}${orderByParam}`,
      {
        method: "get"
      }
    )

    dispatch({
      type: FETCH_COMPANY_BREAKDOWNS,
      payload: {
        ...state,
        paginatedCompanyBreakdownList: mapPaginatedDataToCamelCase<PaginatedData<CompanyBreakDown>>(response.data)
      }
    })
  }

  const updateBlackListStatus = async (id: number, value: boolean) => {
    const response = await axios(`/company/breakdown/update_black_list/`, {
      method: "put",
      data: { id_list: [id], status: value }
    })
    if (!response.data.success) {
      return
    }
    const updatedList = state.paginatedCompanyBreakdownList.results.map((element) => {
      if (element.id === id) {
        element.blackList = value
      }
      return element
    })

    dispatch({
      type: FETCH_COMPANY_BREAKDOWNS,
      payload: {
        ...state,
        paginatedCompanyBreakdownList: { ...state.paginatedCompanyBreakdownList, results: updatedList }
      }
    })
  }

  return (
    <CompaniesContext.Provider
      value={{
        ...state,
        fetchCompanyList,
        fetchCompanyBreakdownList,
        updateBlackListStatus,
        updateCompanyBlackListStatus
      }}
    >
      {children}
    </CompaniesContext.Provider>
  )
}

export default CompaniesContext
